import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PARTICLES, HIGGS } from '../data';
import { ANTIPARTICLE_DATA } from '../antimatterData';

const CANVAS_SIZE = 560;
const CX = CANVAS_SIZE / 2;
const CY = CANVAS_SIZE / 2;

const TIERS = [
  { label: 'Strong coupling', particles: ['z', 'w', 'top'], r: 90, speed: 0.18, color: '#f0c040' },
  { label: 'Medium coupling', particles: ['bottom', 'tau', 'charm'], r: 145, speed: 0.11, color: '#ff9040' },
  { label: 'Weak coupling', particles: ['strange', 'muon'], r: 195, speed: 0.07, color: '#50e0a0' },
  { label: 'Faint coupling', particles: ['up', 'down', 'electron'], r: 245, speed: 0.045, color: '#4a9eff' },
  { label: 'No coupling', particles: ['photon', 'gluon'], r: 295, speed: 0, color: 'rgba(255,255,255,0.2)' },
  { label: 'Uncertain', particles: ['nu_e', 'nu_mu', 'nu_tau'], r: 340, speed: 0.02, color: 'rgba(255,255,255,0.15)' },
];

function catColor(p) {
  if (!p) return '#fff';
  if (p.category === 'quark') return '#4a9eff';
  if (p.category === 'lepton') return '#50e0a0';
  if (p.category === 'higgs') return '#f0c040';
  if (p.id === 'photon') return '#ffffff';
  if (p.id === 'gluon') return '#e060ff';
  return '#ff9040';
}

export default function OrbitalMap({ onSelect, selected, mode }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const anglesRef = useRef({});
  const antiAnglesRef = useRef({});
  const [hovered, setHovered] = useState(null);
  const [showAntimatter, setShowAntimatter] = useState(false);
  const positionsRef = useRef({});
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  useEffect(() => {
    TIERS.forEach(tier => {
      tier.particles.forEach((id, i) => {
        const base = (i / tier.particles.length) * Math.PI * 2;
        anglesRef.current[id] = base;
        antiAnglesRef.current[id] = base + Math.PI;
      });
    });
  }, []);

  const draw = useCallback((ts) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);

    TIERS.forEach(tier => {
      ctx.beginPath(); ctx.arc(CX, CY, tier.r, 0, Math.PI * 2);
      ctx.strokeStyle = tier.color; ctx.lineWidth = tier.speed === 0 ? 0.5 : 0.75;
      ctx.setLineDash(tier.speed === 0 ? [4, 6] : []);
      ctx.globalAlpha = tier.speed === 0 ? 0.2 : 0.25; ctx.stroke();
      ctx.setLineDash([]); ctx.globalAlpha = 1;

      // Antimatter ring (outer, dashed)
      if (showAntimatter) {
        ctx.beginPath(); ctx.arc(CX, CY, tier.r + 18, 0, Math.PI * 2);
        ctx.strokeStyle = tier.color; ctx.lineWidth = 0.4;
        ctx.setLineDash([2, 4]);
        ctx.globalAlpha = 0.12; ctx.stroke();
        ctx.setLineDash([]); ctx.globalAlpha = 1;
      }
    });

    const pulse = 0.7 + 0.3 * Math.sin(ts / 900);
    ctx.beginPath(); ctx.arc(CX, CY, 22 * pulse, 0, Math.PI * 2);
    ctx.fillStyle = accentColor + '25'; ctx.fill();
    ctx.beginPath(); ctx.arc(CX, CY, 16, 0, Math.PI * 2);
    ctx.fillStyle = accentColor; ctx.fill();
    ctx.font = 'bold 11px Space Mono, monospace';
    ctx.fillStyle = '#1a1008'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(isRuach ? 'א' : 'H⁰', CX, CY);

    const newPositions = {};

    TIERS.forEach(tier => {
      tier.particles.forEach(id => {
        const p = PARTICLES.find(x => x.id === id);
        if (!p) return;

        anglesRef.current[id] = (anglesRef.current[id] || 0) + tier.speed * 0.012;
        const angle = anglesRef.current[id];
        const px = CX + Math.cos(angle) * tier.r;
        const py = CY + Math.sin(angle) * tier.r;
        newPositions[id] = { x: px, y: py };

        const color = catColor(p);
        const isSel = selected?.id === id;
        const isHov = hovered === id;
        const radius = isSel ? 11 : isHov ? 10 : 7;

        if (isSel || isHov) {
          const grd = ctx.createRadialGradient(px, py, 0, px, py, radius + 6);
          grd.addColorStop(0, color + '40'); grd.addColorStop(1, color + '00');
          ctx.beginPath(); ctx.arc(px, py, radius + 6, 0, Math.PI * 2);
          ctx.fillStyle = grd; ctx.fill();
        }
        if (tier.speed > 0) {
          ctx.beginPath(); ctx.moveTo(CX, CY); ctx.lineTo(px, py);
          ctx.strokeStyle = color; ctx.lineWidth = isSel ? 1.5 : 0.5;
          ctx.globalAlpha = isSel ? 0.5 : 0.08; ctx.stroke(); ctx.globalAlpha = 1;
        }
        ctx.beginPath(); ctx.arc(px, py, radius, 0, Math.PI * 2);
        ctx.fillStyle = tier.speed === 0 ? 'rgba(255,255,255,0.08)' : color; ctx.fill();
        if (tier.speed === 0) { ctx.strokeStyle = color; ctx.lineWidth = 1; ctx.stroke(); }
        ctx.font = `${isSel ? 'bold ' : ''}9px Space Mono, monospace`;
        ctx.fillStyle = '#fff'; ctx.globalAlpha = isSel || isHov ? 1 : 0.75;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(p.symbol, px, py); ctx.globalAlpha = 1;

        // Antimatter particle
        if (showAntimatter) {
          const anti = ANTIPARTICLE_DATA[id];
          if (!anti || anti.name.includes('self-conjugate')) return;

          antiAnglesRef.current[id] = (antiAnglesRef.current[id] || angle + Math.PI) + tier.speed * 0.012;
          const aAngle = antiAnglesRef.current[id];
          const ax = CX + Math.cos(aAngle) * (tier.r + 18);
          const ay = CY + Math.sin(aAngle) * (tier.r + 18);
          newPositions[`anti_${id}`] = { x: ax, y: ay };

          const isASel = selected?.id === `anti_${id}`;
          ctx.beginPath(); ctx.arc(ax, ay, isASel ? 10 : 7, 0, Math.PI * 2);
          ctx.fillStyle = color + '18'; ctx.fill();
          ctx.strokeStyle = color + (isASel ? 'cc' : '60');
          ctx.lineWidth = isASel ? 1.5 : 0.8;
          ctx.setLineDash([2, 2]); ctx.stroke(); ctx.setLineDash([]);
          ctx.font = '8px Space Mono, monospace';
          ctx.fillStyle = color; ctx.globalAlpha = 0.7;
          ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
          ctx.fillText(anti.symbol, ax, ay); ctx.globalAlpha = 1;
        }
      });
    });

    positionsRef.current = newPositions;
    animRef.current = requestAnimationFrame(draw);
  }, [selected, hovered, showAntimatter, isRuach, accentColor]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  const handleClick = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (CANVAS_SIZE / rect.width);
    const my = (e.clientY - rect.top) * (CANVAS_SIZE / rect.height);
    const dc = Math.sqrt((mx - CX) ** 2 + (my - CY) ** 2);
    if (dc < 20) { onSelect(HIGGS); return; }
    let closest = null, minDist = 18;
    Object.entries(positionsRef.current).forEach(([id, pos]) => {
      const d = Math.sqrt((mx - pos.x) ** 2 + (my - pos.y) ** 2);
      if (d < minDist) { minDist = d; closest = id; }
    });
    if (closest) {
      const isAnti = closest.startsWith('anti_');
      if (isAnti) {
        const baseId = closest.replace('anti_', '');
        const anti = ANTIPARTICLE_DATA[baseId];
        const baseP = PARTICLES.find(p => p.id === baseId);
        if (anti) onSelect({ id: closest, name: anti.name, symbol: anti.symbol, massLabel: anti.mass, category: baseP?.category || 'boson', charge: anti.charge, spin: '1/2', higgsRelation: baseP?.higgsRelation || 'couples', description: anti.science, detail: isRuach ? anti.ruach : anti.science, isAntiparticle: true, antiData: anti });
      } else {
        const p = PARTICLES.find(x => x.id === closest);
        if (p) onSelect(p);
      }
    }
  }, [onSelect, isRuach]);

  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (CANVAS_SIZE / rect.width);
    const my = (e.clientY - rect.top) * (CANVAS_SIZE / rect.height);
    let closest = null, minDist = 18;
    Object.entries(positionsRef.current).forEach(([id, pos]) => {
      const d = Math.sqrt((mx - pos.x) ** 2 + (my - pos.y) ** 2);
      if (d < minDist) { minDist = d; closest = id; }
    });
    setHovered(closest);
  }, []);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.75rem' }}>
        <button onClick={() => setShowAntimatter(s => !s)} style={{
          padding: '5px 16px', borderRadius: '20px', cursor: 'pointer',
          background: showAntimatter ? `${accentColor}25` : 'rgba(255,255,255,0.06)',
          color: showAntimatter ? accentColor : 'rgba(255,255,255,0.4)',
          fontFamily: 'Space Mono, monospace', fontSize: '0.68rem', letterSpacing: '0.08em',
          border: `1px solid ${showAntimatter ? accentColor + '50' : 'rgba(255,255,255,0.1)'}`,
          transition: 'all 0.2s',
        }}>
          {showAntimatter ? '◉' : '○'} {isRuach ? 'Show mirrors — מַרְאוֹת' : 'Show antimatter'}
        </button>
      </div>

      <canvas ref={canvasRef} width={CANVAS_SIZE} height={CANVAS_SIZE}
        onClick={handleClick} onMouseMove={handleMouseMove} onMouseLeave={() => setHovered(null)}
        style={{ width: '100%', maxWidth: `${CANVAS_SIZE}px`, cursor: hovered ? 'pointer' : 'default', display: 'block' }}
      />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'center' }}>
        {TIERS.map(t => (
          <div key={t.label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'Space Mono' }}>
            <div style={{ width: '16px', height: '2px', background: t.color, borderRadius: '1px', opacity: t.speed === 0 ? 0.4 : 0.7 }} />
            {t.label}
          </div>
        ))}
        {showAntimatter && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'Space Mono' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '50%', border: '1px dashed rgba(255,255,255,0.4)' }} />
            {isRuach ? 'Mirrors — antimatter' : 'Antimatter (outer orbit, dashed)'}
          </div>
        )}
      </div>
    </div>
  );
}
